/**
 * pan review reset <id>
 *
 * Human-initiated full pipeline reset. Clears all specialist states,
 * resets the circuit breaker, and re-triggers the review pipeline.
 * No circuit breaker limit — this is a deliberate human override.
 * With --session, also clears the saved Claude session after the
 * review reset completes.
 */

import chalk from 'chalk';
import { getDashboardApiUrlSync } from '../../lib/config.js';
import { resetSessionCommand } from './reset-session.js';

const DASHBOARD_URL = getDashboardApiUrlSync();

export interface ResetReviewOptions {
  session?: boolean;
}

export async function resetReviewCommand(id: string, options: ResetReviewOptions = {}): Promise<void> {
  const issueId = id.toUpperCase();

  console.log(chalk.dim(`Resetting review cycles for ${issueId}...`));

  try {
    const response = await fetch(`${DASHBOARD_URL}/api/review/${issueId}/reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    const result = await response.json() as { success: boolean; message?: string; error?: string; queued?: boolean };

    if (!response.ok) {
      console.error(chalk.red(`\nError: ${result.error || 'Failed to reset review cycles'}`));
      process.exit(1);
    }

    console.log(chalk.green(`\n✓ ${result.message}`));

    if (result.queued) {
      console.log(chalk.dim('  Review-agent will pick this up when available.'));
    }

    if (options.session) {
      // Non-destructive: clears only the resume pointers (session.id / sessions.json)
      // under ~/.overdeck/agents/<id>/ so the next start opens a fresh Claude
      // session. The JSONL transcript history is NEVER touched — that is the truly
      // sacred artifact, and resetSessionCommand does not delete it. Also refuses
      // while the agent is running. Re-enabled after bf77f0194 hard-blocked this and
      // left `pan start` pointing at a dead command (its refusal recommends exactly
      // this). Needed e.g. to switch a stopped agent's model, where the saved session
      // can't be resumed under different provider routing.
      await resetSessionCommand(id);
    }

  } catch (error: any) {
    if (error.code === 'ECONNREFUSED') {
      console.error(chalk.red('\nError: Dashboard not running'));
      console.error(chalk.dim('Start the dashboard with: pan up'));
      process.exit(1);
    }
    console.error(chalk.red(`\nError: ${error.message}`));
    process.exit(1);
  }
}
